import { Group, Select, Stack, Text, Textarea } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { DatePickerInput, TimeInput, DatesRangeValue } from "@mantine/dates"
import dayjs from "dayjs"
import { Form } from "@components/typed-form"
import { DefaultError } from "@components/error-context"
import { mantineTimeToTimeOnly } from "@/utils/mantine-time-to-time-only"
import { CancelButton, SubmitButton } from "@components/Buttons"
import { DateOnly } from "@/@client/System"
import {
  hasValidationErrors,
  tryGetValidationErrorsAsMantineErrors,
} from "@/utils/to-mantine-validation-errors"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { RenderSimplePerson } from "@/blocks/persons-and-users/User"
import { dateToDateOnly } from "@/utils/date-to-date-only"
import { useRequestablePresenceShiftTypes } from "../../../../../../blocks/shift-types/useRequestablePresenceShiftTypes"

interface Props {
  departmentId: number
  contractId: number
  shiftTypeId: number
  date: DateOnly
  onAdded: () => void
  onCancel: () => void
}

export function AddPersonStatusShiftModalContent({
  departmentId,
  contractId,
  shiftTypeId,
  date,
  onAdded,
  onCancel,
}: Props) {
  const { data: availablePresenceShiftTypes } =
    useRequestablePresenceShiftTypes({ departmentId })
  const shiftType = availablePresenceShiftTypes.find(
    (v) => v.shiftTypeId == shiftTypeId
  )
  const { t } = useTranslation("app_server")
  return (
    <Form
      url="/api/Orchestra/AddPersonStatusShift"
      onSuccess={() => onAdded()}
      onError={(e, _, __, f) => {
        if (e instanceof DefaultError && hasValidationErrors(e)) {
          const validationErrors = tryGetValidationErrorsAsMantineErrors(e, t)
          if (validationErrors) {
            f.setErrors(validationErrors)
          }
        }
        showErrorNotification(e)
      }}
      initialValues={{
        shiftTypeId: shiftTypeId.toString(),
        dateRange: [
          dayjs(date).set("h", 0).set("m", 0).set("s", 0).toDate(),
          dayjs(date).set("h", 0).set("m", 0).set("s", 0).toDate(),
        ] as DatesRangeValue,
        startTime: shiftType?.startTimeDefault1 ?? null,
        endTime: shiftType?.endTimeDefault1 ?? null,
        note: null as any as string,
      }}
      transform={(v) => {
        const startDate = v.dateRange[0]
          ? dateToDateOnly(new Date(v.dateRange[0]))
          : null
        const endDate = v.dateRange[1]
          ? dateToDateOnly(new Date(v.dateRange[1]))
          : null

        return {
          contractId: contractId,
          shiftTypeId: parseInt(v.shiftTypeId),
          startDate: startDate!,
          endDate: endDate!,
          note: v.note,
          startTime: v.startTime ? mantineTimeToTimeOnly(v.startTime) : null,
          endTime: v.endTime ? mantineTimeToTimeOnly(v.endTime) : null,
          departmentId,
        }
      }}
    >
      {(f, _, m) => {
        // eslint-disable-next-line security/detect-object-injection
        const errors = Object.keys(f.errors).map((k) => f.errors[k])
        return (
          <Stack>
            {shiftTypeId}
            <Text fw={700}>
              <RenderSimplePerson suspense contractId={contractId} />
            </Text>
            <DatePickerInput
              type="range"
              label={t("TimePeriod")}
              placeholder={t("TimePeriod")}
              {...f.getInputProps(_.dateRange._PATH_)}
            />
            <Group>
              <TimeInput
                label={t("StartTime")}
                {...f.getInputProps(_.startTime._PATH_)}
              />
              <TimeInput
                label={t("EndTime")}
                {...f.getInputProps(_.endTime._PATH_)}
              />
            </Group>
            <Select
              label={t("ShiftType")}
              data={availablePresenceShiftTypes.map((v) => ({
                label: v.name,
                value: v.shiftTypeId.toString(),
              }))}
              {...f.getInputProps(_.shiftTypeId._PATH_)}
            />
            <Textarea label={t("Note")} {...f.getInputProps(_.note._PATH_)} />

            <Group justify="space-between" mt="md">
              <CancelButton onClick={onCancel} />
              <SubmitButton disabled={errors.length > 0} loading={m.isPending}>
                {t("EnterShift")}
              </SubmitButton>
            </Group>
          </Stack>
        )
      }}
    </Form>
  )
}
